import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface SalesDataItem {
  segment: string;
  model: string;
  postal_district: string;
  dealer_code: string;
  sales_category: string;
  amount_sold: number;
}

export interface ManufacturerSalesData {
  id?: number;
  manufacturer: string;
  pma: string;
  year: number;
  month: number;
  sales_data: SalesDataItem[];
  created_at?: string;
  updated_at?: string;
}

export interface BulkImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: Array<{
    row: number;
    error: string;
  }>;
}

export interface ManufacturerSalesMetadata {
  manufacturers: string[];
  pmas: string[];
  years: number[];
  totalRecords: number;
}

export interface ManufacturerSalesSummary {
  totalRecords: number;
  totalSalesItems: number;
  totalAmountSold: number;
  dateRange: {
    earliest: string;
    latest: string;
  };
}

class ManufacturerSalesService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get all manufacturer sales data with optional filters
   */
  async getAll(filters?: {
    manufacturers?: string[];
    pmas?: string[];
    years?: number[];
    months?: number[];
    limit?: number;
    offset?: number;
  }): Promise<{ data: ManufacturerSalesData[]; total: number }> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.manufacturers?.length) {
        params.append('manufacturers', filters.manufacturers.join(','));
      }
      if (filters?.pmas?.length) {
        params.append('pmas', filters.pmas.join(','));
      }
      if (filters?.years?.length) {
        params.append('years', filters.years.join(','));
      }
      if (filters?.months?.length) {
        params.append('months', filters.months.join(','));
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters?.offset) {
        params.append('offset', filters.offset.toString());
      }

      const response = await axios.get(
        `${API_BASE_URL}/manufacturer-sales?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return {
          data: response.data.data,
          total: response.data.total
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch data');
      }
    } catch (error: any) {
      console.error('Error fetching manufacturer sales data:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch data');
    }
  }

  /**
   * Get a single record by ID
   */
  async getById(id: number): Promise<ManufacturerSalesData> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/manufacturer-sales/${id}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch data');
      }
    } catch (error: any) {
      console.error('Error fetching manufacturer sales data:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch data');
    }
  }

  /**
   * Bulk import from CSV data
   */
  async bulkImport(headers: string[], rows: string[][], year?: number): Promise<BulkImportResult> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/manufacturer-sales/bulk-import`,
        { headers, rows, year },
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('Error importing manufacturer sales data:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to import data');
    }
  }

  /**
   * Get metadata for filters
   */
  async getMetadata(): Promise<ManufacturerSalesMetadata> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/manufacturer-sales/metadata`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch metadata');
      }
    } catch (error: any) {
      console.error('Error fetching metadata:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch metadata');
    }
  }

  /**
   * Get summary statistics
   */
  async getSummary(filters?: {
    manufacturers?: string[];
    years?: number[];
  }): Promise<ManufacturerSalesSummary> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.manufacturers?.length) {
        params.append('manufacturers', filters.manufacturers.join(','));
      }
      if (filters?.years?.length) {
        params.append('years', filters.years.join(','));
      }

      const response = await axios.get(
        `${API_BASE_URL}/manufacturer-sales/summary?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch summary');
      }
    } catch (error: any) {
      console.error('Error fetching summary:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch summary');
    }
  }

  /**
   * Delete a record
   */
  async delete(id: number): Promise<boolean> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/manufacturer-sales/${id}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete');
      }
    } catch (error: any) {
      console.error('Error deleting manufacturer sales data:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete');
    }
  }
}

export default new ManufacturerSalesService();

